import { apiClient } from '@/lib/api-client';

export type DocumentType = 'FACTURA' | 'BOLETA' | 'NOTA_CREDITO' | 'NOTA_DEBITO';
export type DocumentStatus = 'DRAFT' | 'ISSUED' | 'SIGNED' | 'VOIDED';
export type SunatStatus = 'PENDING' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'OBSERVED';
export type TaxCategory = 'GRAVADO' | 'EXONERADO' | 'INAFECTO' | 'EXPORTACION' | 'GRATUITO';
export type CustomerDocType = 'DNI' | 'RUC' | 'CE' | 'PASAPORTE' | 'OTROS';

export interface DocLine {
  id: string;
  position: number;
  description: string;
  unitCode: string;
  quantity: string;
  unitPrice: string;
  taxCategory: TaxCategory;
  netAmount: string;
  igvAmount: string;
  lineTotal: string;
}

export interface DocumentSummary {
  id: string;
  type: DocumentType;
  fullNumber: string;
  issueDate: string;
  customerDocType: string;
  customerDocNumber: string;
  customerName: string;
  currency: string;
  grandTotal: string;
  status: DocumentStatus;
  sunatStatus: SunatStatus;
  sunatCode: string | null;
  sunatMessage: string | null;
  createdAt: string;
}

export interface DocumentDetail extends DocumentSummary {
  taxableSubtotal: string;
  exoneratedTotal: string;
  unaffectedTotal: string;
  igvTotal: string;
  customerAddress: string | null;
  customerEmail: string | null;
  notes: string | null;
  lines: DocLine[];
}

export interface DocumentListResponse {
  items: DocumentSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DocumentLineInput {
  productId?: string | null;
  description: string;
  unitCode: string;
  quantity: number;
  unitPrice: number;
  taxCategory: TaxCategory;
}

export interface DocumentCreateInput {
  type: DocumentType;
  seriesCode?: string;
  currency: 'PEN' | 'USD';
  customer: {
    docType: CustomerDocType;
    docNumber: string;
    name: string;
    address?: string | null;
    email?: string;
  };
  lines: DocumentLineInput[];
  notes?: string;
}

export async function listDocuments(params: {
  page?: number;
  pageSize?: number;
  search?: string;
  type?: DocumentType;
  sunatStatus?: SunatStatus;
} = {}): Promise<DocumentListResponse> {
  const { data } = await apiClient.get<DocumentListResponse>('/documents', { params });
  return data;
}

export async function getDocument(id: string): Promise<DocumentDetail> {
  const { data } = await apiClient.get<DocumentDetail>(`/documents/${id}`);
  return data;
}

export async function createDocument(
  input: DocumentCreateInput,
  emit = true,
): Promise<{ id: string; fullNumber: string; status: string; sunatStatus: string }> {
  const { data } = await apiClient.post<{
    id: string;
    fullNumber: string;
    status: string;
    sunatStatus: string;
  }>(`/documents?emit=${emit ? 'true' : 'false'}`, input);
  return data;
}

export async function resendDocument(id: string): Promise<void> {
  await apiClient.post(`/documents/${id}/send`);
}

export interface NoteReason {
  code: string;
  label: string;
}

export async function getNoteReasons(): Promise<{
  creditNote: NoteReason[];
  debitNote: NoteReason[];
}> {
  const { data } = await apiClient.get<{ creditNote: NoteReason[]; debitNote: NoteReason[] }>(
    '/documents/catalogs/note-reasons',
  );
  return data;
}

export async function createNote(input: {
  affectedDocumentId: string;
  type: 'NOTA_CREDITO' | 'NOTA_DEBITO';
  reasonCode: string;
  reasonText?: string;
  notes?: string;
}): Promise<{ id: string; fullNumber: string }> {
  const { data } = await apiClient.post<{ id: string; fullNumber: string }>(
    '/documents/notes?emit=true',
    input,
  );
  return data;
}

export async function voidDocument(
  id: string,
  reason: string,
): Promise<{ documentId: string; voidedAt: string; summaryId: string | null }> {
  const { data } = await apiClient.post<{
    documentId: string;
    voidedAt: string;
    summaryId: string | null;
  }>(`/documents/${id}/void`, { reason });
  return data;
}

/** URL absoluta para abrir el PDF en una nueva pestaña. */
export function pdfUrl(id: string, format: 'A4' | 'ticket80' = 'A4'): string {
  return `/api/documents/${id}/pdf?format=${format}`;
}
